import type { LoanTerms } from '@hyphengroup/cms-components/calculatedAttributes/store';
import {
  CmsOffer,
  GiftListItem,
} from '@hyphengroup/cms-components/types/cmsProduct';
import { currencyFormat } from '@hyphengroup/cms-components/utils/helpers/formatters';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import type { LoanSearchParams } from '@hyphengroup/cms-components/utils/searchParams/loanSearchParams';
import { isAfter, isBefore } from 'date-fns';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { cmsQueryParamsPrefix } from '$/components/cms/helpers/types';

export type ApplyStep = 'capture' | 'provider' | 'bundle-upsell';

export function filterActiveOffers(
  offers: CmsOffer[] | undefined,
  now: Date = new Date(),
): CmsOffer[] {
  return (offers ?? []).filter((offer) => {
    const start = offer.period?.start_date;
    const end = offer.period?.end_date;
    if (start && isBefore(now, new Date(start))) return false;
    if (end && isAfter(now, new Date(end))) return false;
    return true;
  });
}

export function getActiveBundleOffers(
  product: CmsApplyRewardProduct,
  now: Date = new Date(),
): CmsOffer[] {
  return filterActiveOffers(
    product.catalogueProduct?.bundle_exclusive_offers,
    now,
  );
}

export function getActiveStandaloneOffers(
  product: CmsApplyRewardProduct,
  now: Date = new Date(),
): CmsOffer[] {
  return filterActiveOffers(product.catalogueProduct?.exclusive_offers, now);
}

export function getBundleOfferId(offer: CmsOffer): string {
  return offer.uid ?? offer.title ?? offer.offer_title;
}

export function getBundleOfferTrackingId(offer: CmsOffer): string {
  return offer.uid ?? '';
}

export function getApplyNextStep(product: CmsApplyRewardProduct): ApplyStep {
  return getActiveBundleOffers(product).length > 0
    ? 'bundle-upsell'
    : 'provider';
}

const LOAN_KEYS = [
  'amount',
  'tenure',
] as const satisfies readonly (keyof LoanTerms & keyof LoanSearchParams)[];

/**
 * Query for the lead and the redirect (CMS-2052): the given loan as `amount` /
 * `tenure`, the names OG Rewards reads, or no loan at all. A loan already in
 * `search`, under either name, is dropped.
 */
export function buildApplySearch(search: string, loan?: LoanTerms): string {
  const params = new URLSearchParams(search);
  for (const key of LOAN_KEYS) {
    // The filter's name: the same prefix it writes with, so a rename follows.
    params.delete(`${cmsQueryParamsPrefix}${key}`);
    // On the apply page `loan` was read from these very names, so they go back
    // unchanged; dropping them first keeps a half or invalid pair from leaking.
    params.delete(key);
    if (loan) params.set(key, String(loan[key]));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * Builds the apply redirect URL, forwarding the current query string.
 *
 * When `useSecondUrlSet` is true (the V3 form "No thanks, just apply for card"
 * journey, CMS-1798) it appends `journey=nobundle` so the redirect handler
 * resolves against the RG 2nd URL set. `journey` is a named dimension (further
 * values may be added later); `nobundle` matches the value RG uses.
 */
export function buildApplyRedirectUrl(
  langPrefix: string,
  productId: number,
  search: string,
  useSecondUrlSet = false,
): string {
  const params = new URLSearchParams(search);
  if (useSecondUrlSet) params.set('journey', 'nobundle');
  const query = params.toString();
  return `${langPrefix}/apply/${productId}/redirect${query ? `?${query}` : ''}`;
}

export function getGiftDisplayName(
  giftListItem: GiftListItem | undefined,
): string | undefined {
  const gift = giftListItem?.gift;
  return gift?.gift_title;
}

export function getGiftDisplayLabel(
  giftListItem: GiftListItem | undefined,
): string | undefined {
  const gift = giftListItem?.gift;
  if (!gift) return undefined;

  const value = gift.gift_value;
  const price = value?.topup_amount ?? value?.price;
  const prefix = value?.prefix ?? '';
  const priceLabel = price ? ` — ${prefix}${price}` : '';

  return `${gift.gift_title}${priceLabel}`;
}

export function getBundleRewardLabel(offer: CmsOffer): string | undefined {
  return getGiftDisplayLabel(offer.list_of_gifts?.[0]);
}

export function getBundleRewardName(offer: CmsOffer): string | undefined {
  return getGiftDisplayName(offer.list_of_gifts?.[0]);
}

export function getStandaloneRewardName(
  product: CmsApplyRewardProduct,
): string | undefined {
  return getGiftDisplayName(
    getActiveStandaloneOffers(product)[0]?.list_of_gifts?.[0],
  );
}

export function getBundlePreviewOffer(
  product: CmsApplyRewardProduct,
  selectedOffer: CmsOffer | undefined,
): CmsOffer | undefined {
  return selectedOffer ?? getActiveBundleOffers(product)[0];
}

export function getHighestBundleRewardDisplay(
  product: CmsApplyRewardProduct,
): string | undefined {
  const allGifts = getActiveBundleOffers(product).flatMap(
    (offer) => offer.list_of_gifts ?? [],
  );

  const prices = allGifts
    .map((gl) => gl?.gift?.gift_value?.price)
    .filter((p): p is number => typeof p === 'number' && p > 0);

  if (prices.length === 0) return undefined;

  const maxPrice = Math.max(...prices);
  const prefix = allGifts.find((gl) => gl?.gift?.gift_value?.prefix)?.gift
    ?.gift_value?.prefix;

  return `${currencyFormat(prefix)}${currencyFormatterNoDecimal.format(maxPrice)}`;
}
